import Promise from 'bluebird';
import _ from 'lodash';
import 'whatwg-fetch';
import { ApiResponse, ApiResponseModel } from 'models/Api/ApiResponse';
import { HttpRequestHeader, JSONObject, HttpRequestConfiguration } from 'models/Api/types';
import { CredentialService } from './Credential';

interface Interceptor {
  request?: (config: HttpRequestConfiguration) => HttpRequestConfiguration;
  response?: (response: Response) => Response;
  error?: (error: any) => any;
}

export class AjaxService {
  private static interceptors: Interceptor[] = [];

  public static addInterceptor(interceptor: Interceptor): void {
    this.interceptors.push(interceptor);
  }

  public static removeInterceptor(interceptor: Interceptor): void {
    const index = this.interceptors.indexOf(interceptor);
    if (index !== -1) {
      this.interceptors.splice(index, 1);
    }
  }

  public static postData(url: string, data: JSONObject = {}, requireAuth: boolean = true, withCredentials: boolean = false, useRefreshToken: boolean = false, headerOverrides: HttpRequestHeader = {}): Promise<ApiResponse<any>> {
    const headers = this.fetchHeader(requireAuth, useRefreshToken);
    _.assign(headers, headerOverrides);

    return this.callAjax('POST', url, data, headers, withCredentials)
  }

  public static getData(url: string, data: JSONObject = {}, requireAuth: boolean = true, withCredentials: boolean = false, headerOverrides: HttpRequestHeader = {}): Promise<ApiResponse<any>> {
    const headers = this.fetchHeader(requireAuth);
    _.assign(headers, headerOverrides);

    return this.callAjax('GET', url, data, headers, withCredentials);
  }

  public static patchData(url: string, data: JSONObject = {}, requireAuth: boolean = true, withCredentials: boolean = false, headerOverrides: HttpRequestHeader = {}): Promise<ApiResponse<any>> {
    const headers = this.fetchHeader(requireAuth);
    _.assign(headers, headerOverrides);

    return this.callAjax('PATCH', url, data, headers, withCredentials);
  }

  public static putData(url: string, data: JSONObject = {}, requireAuth: boolean = true, withCredentials: boolean = false, headerOverrides: HttpRequestHeader = {}): Promise<ApiResponse<any>> {
    const headers = this.fetchHeader(requireAuth);
    _.assign(headers, headerOverrides);

    return this.callAjax('PUT', url, data, headers, withCredentials);
  }

  public static delData(url: string, data: JSONObject = {}, requireAuth: boolean = true, withCredentials: boolean = false, headerOverrides: HttpRequestHeader = {}): Promise<ApiResponse<any>> {
    const headers = this.fetchHeader(requireAuth);
    _.assign(headers, headerOverrides);

    return this.callAjax('DELETE', url, data, headers, withCredentials);
  }

  private static verifyParameters(...args: string[]): boolean {
    const invalidValues = [null, undefined, ''];
    return !args.filter(arg => invalidValues.map(val => val === arg).reduceRight((a, b) => a || b))
      .length;
  }

  private static fetchHeader(requireAuth: boolean = true, useRefreshToken: boolean = false): HttpRequestHeader {
    const header: HttpRequestHeader = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };

    if (requireAuth) {
      const token = useRefreshToken ? CredentialService.fetchRefreshToken() : CredentialService.fetchAuthToken();
      if (token) {
        header['Authorization'] = `Bearer ${token}`;
      }
    }

    return header;
  }

  private static callAjax(method: string, url: string, data: JSONObject = {}, headers: HttpRequestHeader = {}, withCredentials: boolean = false, request: any = fetch): Promise<ApiResponse<any>> {
    method = method ? method.toUpperCase() : 'GET';

    const cfg: HttpRequestConfiguration = {
      mode: 'cors',
      headers,
      method,
    };

    if (!_.isEmpty(data)) {
      if (method !== 'GET' && method !== 'HEAD') {
        cfg.body = JSON.stringify(data);
      } else {
        url += `?${this.serialize(data)}`;
      }
    }

    if (withCredentials) {
      cfg.credentials = 'include';
    }

    return this.executeRequest(request, url, cfg);
  }

  private static executeRequest(request: any, url: string, cfg: HttpRequestConfiguration): Promise<ApiResponse<any>> {
    return new Promise<ApiResponse<any>>((resolve, reject) => {
      // Apply request interceptors
      let modifiedConfig = { ...cfg };
      this.interceptors.forEach(interceptor => {
        if (interceptor.request) {
          modifiedConfig = interceptor.request(modifiedConfig);
        }
      });

      request(url, modifiedConfig)
        .then((response: Response) => {
          // Apply response interceptors
          let modifiedResponse = response;
          this.interceptors.forEach(interceptor => {
            if (interceptor.response) {
              modifiedResponse = interceptor.response(modifiedResponse);
            }
          });

          const jsonRegExp: RegExp = /application\/json/i;
          const headers: Headers = modifiedResponse.headers;
          const isJson: boolean = jsonRegExp.test(headers.get('Content-Type') || '');

          if (!modifiedResponse.ok) {
            // Handle 401 Unauthorized
            if (modifiedResponse.status === 401) {
              // Clear credentials
              CredentialService.clearAll();
              // Redirect to auth page
              window.location.href = '/auth/sign-in';
              const err = new Error('Unauthorized access');
              reject(err);
              return;
            }
            
            // For other errors, try to parse the response body to get the error message
            if (isJson) {
              return modifiedResponse.json().then((errorData: any) => {
                const errorMessage = errorData?.message || errorData?.error || modifiedResponse.statusText;
                const err = new Error(errorMessage);
                // Attach the full error response for better error handling
                (err as any).response = errorData;
                (err as any).status = modifiedResponse.status;
                reject(err);
                return;
              }).catch(() => {
                // If JSON parsing fails, fall back to status text
                const err = new Error(modifiedResponse.statusText);
                reject(err);
                return;
              });
            } else {
              const err = new Error(modifiedResponse.statusText);
              reject(err);
              return;
            }
          }

          if (modifiedResponse.status === 204 || !isJson) {
            return {};
          }

          return modifiedResponse.json() as Promise<ApiResponse<any>>;
        })
        .then((json: ApiResponse<any>) => {
          const apiResponse = new ApiResponseModel(json.data, json.status, json.ok, json.message);
          resolve(apiResponse)
        })
        .catch((error: any) => {
          // Apply error interceptors
          let modifiedError = error;
          this.interceptors.forEach(interceptor => {
            if (interceptor.error) {
              modifiedError = interceptor.error(modifiedError);
            }
          });

          // If error already has a message (from our error handling above), use it
          if (modifiedError.message) {
            reject(modifiedError);
            return;
          }

          const err = new Error(modifiedError.message || 'Network error occurred');
          reject(err);
          return;
        });
    });
  }

  private static serialize(obj: any): string {
    const query: string[] = [];

    for (const p in obj) {
      if (obj.hasOwnProperty(p)) {
        query.push(encodeURIComponent(p) + '=' + encodeURIComponent(obj[p]));
      }
    }

    return query.join('&');
  }
}
